import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Form, InputGroup } from 'react-bootstrap';

import BarraInferiorBotoes from '../../../../../components/BarraInferiorBotoes';
import BoxConteudo from '../../../../../components/BoxConteudo';
import CustomButton from '../../../../../components/CustomButton';
import CustomCol from '../../../../../components/CustomCol';
import CustomInputInt from '../../../../../components/CustomInputs/CustomInputInt';
import CustomInputMoney from '../../../../../components/CustomInputs/CustomInputMoney';
import CustomOffcanvas from '../../../../../components/CustomOffcanvas';
import CustomRow from '../../../../../components/CustomRow';
import Input from '../../../../../components/InputNew';
import Select from '../../../../../components/InputNew/Select';
import Checkbox from '../../../../../components/Inputs/Checkbox';
import Log from '../../../../../components/Log/Log';
import Tabela from '../../../../../components/Tabela';
import WorkspaceBotoes from '../../../../../components/WorkspaceBotoes';
import WorkspaceFicha from '../../../../../components/WorkspaceFicha';
import { EnumOption, LogData } from '../../../../../modules/types/types';
import { useTipoCota } from '../../../../../services/enum/useTipoCota';
import {
  createProaqItem,
  deleteProaqItem,
  getItensByProaqId,
  updateProaqItem,
} from '../../../../../services/processosAquisitivos/proaqItemService';
import theme from '../../../../../theme';
import {
  formatarNumero,
  formatDate,
  formatDateIntl,
  formatDateSimp,
  formatoMoney,
  formatValorDinheiro,
  isDataMaiorOuIgual,
  sanitizarForm,
  formataNomeMeioSiglas,
} from '../../../../../utils/functions';
import {
  swalAttention,
  swalAtualizacao,
  swalDelete,
  swalDeleteConfirm,
  swalErro,
  swalSave,
} from '../../../../../utils/swal';
import { headersProaqItens, valoresExibidosProaq } from '../../auxiliarProaq';

interface ItensAquisicaoProps {
  fichaID: string;
  userID: string;
  denominacaoGenericaId: number | null;
  kitDeProdutoDAFId: number | null;
  produtosDAFOptions: EnumOption[];
  onItensChange: (itens: any[]) => void;
}

const ItensAquisicao: React.FC<ItensAquisicaoProps> = ({
  fichaID,
  userID,
  produtosDAFOptions,
  onItensChange,
}) => {
  const TipoCota = useTipoCota();

  const [itensData, setItensData] = useState<any[]>([]);
  const [showModalItem, setShowModalItem] = useState<boolean>(false);
  const [updatingProaqItem, setUpdatingProaqItem] = useState<boolean>(false);
  const [logProaqItemData, setLogProaqItemData] = useState<LogData>();
  const [numeroProximoItem, setNumeroProximoItem] = useState<number>(1);
  const [produtoId, setProdutoId] = useState<string>('');

  const [formProaqItem, setFormProaqItem] = useState({
    id: '',
    numeroItem: 1,
    tipoCota: '',
    cmmInicial: '',
    cmmFim: '',
    cmmValorReferencia: '',
    qtdAdministrativa: '',
    qtdJudicial: '',
    qtdEstrategico: '',
    qtdOutro: '',
    valorPercentualEstrategico: '',
    isValorPercentualEstrategico: false,
    qtdTotal: '',
    fatorEmbalagem: '',
    qtdEmbalagem: '',
    coberturaDias: '',
    coberturaMeses: '',
    valorUntEmbalagem: '',
    valorTotalEstimado: '',
    observacoes: '',
    produto: '',
    produtoDAFId: 0,
    usuarioId: userID,
    proaqId: parseInt(fichaID, 10),
  });

  const fetchItens = async () => {
    if (fichaID === 'undefined') return;
    try {
      const proaqItensData = await getItensByProaqId(fichaID);
      if (proaqItensData) {
        setItensData(proaqItensData);
        onItensChange(proaqItensData);
      }
    } catch (error) {
      console.error('Erro ao buscar itens:', error);
    }
  };

  useEffect(() => {
    fetchItens();
  }, [fichaID]);

  const atualizaProxItem = () => {
    if (itensData.length > 0) {
      const numeroItemMax = itensData[itensData.length - 1].numeroItem;
      setNumeroProximoItem(numeroItemMax + 1);
    } else {
      setNumeroProximoItem(1);
    }
  };

  useEffect(() => {
    atualizaProxItem();
  }, [itensData]);

  function transformValorParaFloat(valor: string): number {
    if (!valor) {
      return 0;
    }
    return parseFloat(valor.replace(/\./g, ''));
  }

  const calculaCoberturaDiasEMeses = useMemo(() => {
    const { cmmValorReferencia, qtdAdministrativa } = formProaqItem;
    const valorReferencia = transformValorParaFloat(cmmValorReferencia);
    const quantidade = transformValorParaFloat(qtdAdministrativa);

    let coberturaDias = 0;
    let coberturaMeses = 0;

    if (valorReferencia > 0) {
      coberturaDias = quantidade / (valorReferencia / 30);
      coberturaMeses = coberturaDias / 30;
    }

    return {
      coberturaDias: formatarNumero(Math.trunc(coberturaDias).toString()),
      coberturaMeses: formatarNumero(Math.trunc(coberturaMeses).toString()),
    };
  }, [formProaqItem.cmmValorReferencia, formProaqItem.qtdAdministrativa]);

  const onClickCalculaQtdAdquirida = useCallback(() => {
    const { cmmValorReferencia } = formProaqItem;
    const valorReferencia = transformValorParaFloat(cmmValorReferencia);
    if (valorReferencia === 0) return;

    const consumo_medio_diario = valorReferencia / 30;
    setFormProaqItem((prev) => ({
      ...prev,
      qtdAdministrativa: formatarNumero(
        Math.ceil(consumo_medio_diario * 365.5).toString(),
      ),
    }));
  }, [formProaqItem]);

  const onClickCalculaCmmReferencia = useCallback(() => {
    const { qtdAdministrativa } = formProaqItem;
    const qtdAdquirida = transformValorParaFloat(qtdAdministrativa);
    if (qtdAdquirida === 0) return;

    const consumo_medio_diario = qtdAdquirida / 365.5;
    setFormProaqItem((prev) => ({
      ...prev,
      cmmValorReferencia: formatarNumero(
        Math.ceil(consumo_medio_diario * 30).toString(),
      ),
    }));
  }, [formProaqItem]);

  const calculaQtdEmbalagem = useMemo(() => {
    const { qtdAdministrativa, fatorEmbalagem } = formProaqItem;

    const fator = transformValorParaFloat(fatorEmbalagem.toString());
    const quantidade = transformValorParaFloat(qtdAdministrativa);

    let embalagens = 0;

    if (fator > 0) {
      embalagens = quantidade / fator;
    }

    return formatarNumero(Math.ceil(embalagens).toString());
  }, [formProaqItem.qtdAdministrativa, formProaqItem.fatorEmbalagem]);

  const calculaQuantidadeTotalItens = useMemo(() => {
    const { qtdAdministrativa, qtdEstrategico, qtdOutro, qtdJudicial } =
      formProaqItem;
    const qtdAdministrativaNumber = transformValorParaFloat(qtdAdministrativa);
    const qtdJudicialNumber = transformValorParaFloat(qtdJudicial);
    const qtdEstrategicoNumber = transformValorParaFloat(qtdEstrategico);
    const qtdOutroNumber = transformValorParaFloat(qtdOutro);

    const total =
      qtdAdministrativaNumber +
      qtdJudicialNumber +
      qtdEstrategicoNumber +
      qtdOutroNumber;

    return formatarNumero(Math.ceil(total).toString());
  }, [
    formProaqItem.qtdAdministrativa,
    formProaqItem.qtdEstrategico,
    formProaqItem.qtdJudicial,
    formProaqItem.qtdOutro,
  ]);

  const calculaValorTotalEstimado = useMemo(() => {
    const { qtdTotal, valorUntEmbalagem } = formProaqItem;
    const valorUnitario = parseFloat(valorUntEmbalagem);
    const quantidade = transformValorParaFloat(qtdTotal);

    let valorTotal = 0;

    if (valorUnitario > 0) {
      valorTotal = quantidade * valorUnitario;
    }

    return valorTotal.toFixed(2);
  }, [formProaqItem.qtdTotal, formProaqItem.valorUntEmbalagem]);

  useEffect(() => {
    const { qtdAdministrativa, valorPercentualEstrategico } = formProaqItem;
    const qtdAdministrativaFloat = transformValorParaFloat(qtdAdministrativa);
    const valorPercentualEstrategicoFloat = transformValorParaFloat(
      valorPercentualEstrategico,
    );
    if (qtdAdministrativaFloat === 0) return;

    const qtdEstoqueEstrategico =
      (qtdAdministrativaFloat * valorPercentualEstrategicoFloat) / 100;

    setFormProaqItem((prev) => ({
      ...prev,
      qtdEstrategico: formatarNumero(
        Math.ceil(qtdEstoqueEstrategico).toString(),
      ),
    }));
  }, [
    formProaqItem.valorPercentualEstrategico,
    formProaqItem.isValorPercentualEstrategico,
  ]);

  useEffect(() => {
    setFormProaqItem((prev) => ({
      ...prev,
      coberturaDias: calculaCoberturaDiasEMeses.coberturaDias,
      coberturaMeses: calculaCoberturaDiasEMeses.coberturaMeses,
      qtdEmbalagem: calculaQtdEmbalagem.toString(),
      qtdTotal: calculaQuantidadeTotalItens,
      valorTotalEstimado: calculaValorTotalEstimado,
    }));
  }, [
    calculaCoberturaDiasEMeses,
    calculaQtdEmbalagem,
    calculaQuantidadeTotalItens,
    calculaValorTotalEstimado,
  ]);

  const tableDataItem = itensData.map((item) => {
    return {
      id: item.id,
      item: item.numeroItem,
      produto: item.produtoDAF.produtoFarmaceutico,
      tipoCota: TipoCota[item.tipoCota].label,
      cmmInicio: item.cmmInicio ? formatDateSimp(item.cmmInicio) : '-',
      cmmFinal: item.cmmFinal ? formatDateSimp(item.cmmFinal) : '-',
      qtdTotal:
        formatarNumero(
          (
            parseInt(item.qtdAdministrativa, 10) +
            parseInt(item.qtdJudicial, 10) +
            parseInt(item.qtdEstrategico, 10)
          ).toString(),
        ) || '',
      qtdAdministrativa:
        formatarNumero(item.qtdAdministrativa.toString()) || '0',
      qtdJudicial: formatarNumero(item.qtdJudicial.toString()) || '0',
      qtdEstrategico: formatarNumero(item.qtdEstrategico.toString()) || '0',
      valorUntEmbalagem: item.valorUntEmbalagem
        ? `R$ ${formatValorDinheiro(parseFloat(item.valorUntEmbalagem).toFixed(2))}`
        : '-',
      valorTotalEstimado:
        item.valorTotalEstimado && item.valorTotalEstimado != 0
          ? `R$ ${formatValorDinheiro(parseFloat(item.valorTotalEstimado).toFixed(2))}`
          : '-',
    };
  });

  const offCanvasDataItem = itensData.map((item) => {
    return {
      id: item.id,
      dataRegistro:
        formatDate(item.dataRegistro) +
        ' - ' +
        formataNomeMeioSiglas(item.usuarioRegistro.nomeCompleto),
      dataUltimaAtualizacao:
        formatDate(item.dataUltimaAtualizacao) +
        ' - ' +
        formataNomeMeioSiglas(item.usuarioAtualizacao.nomeCompleto),

      dataUltimaVisualizacao:
        formatDate(item.dataUltimaVisualizacao) +
        ' - ' +
        formataNomeMeioSiglas(item.usuarioUltimaVisualizacao.nomeCompleto),
      nEdicoes: item.nEdicoes,
      nVisualizacoes: item.nVisualizacoes,
      separadorLog: '',
      produto: <b>{item.produtoDAF.produtoFarmaceutico}</b>,
      tipoCota: TipoCota[item.tipoCota].label,
      separadorItem: '',
      cmmInicio: item.cmmInicio ? formatDateSimp(item.cmmInicio) : '-',
      cmmFinal: item.cmmFinal ? formatDateSimp(item.cmmFinal) : '-',
      cmmValorReferencia: item.cmmValorReferencia
        ? formatarNumero(item.cmmValorReferencia)
        : '-',
      separadorItemCmm: '',
      qtdAdministrativa: item.qtdAdministrativa
        ? formatarNumero(item.qtdAdministrativa.toString())
        : 0,
      qtdJudicial: item.qtdJudicial
        ? formatarNumero(item.qtdJudicial.toString())
        : 0,
      qtdEstrategico: item.qtdEstrategico
        ? formatarNumero(item.qtdEstrategico.toString())
        : 0,
      qtdOutro: item.qtdOutro ? formatarNumero(item.qtdOutro.toString()) : 0,
      qtdTotal: (
        <b>
          {formatarNumero(
            (
              parseInt(item.qtdAdministrativa, 10) +
              parseInt(item.qtdJudicial, 10) +
              parseInt(item.qtdEstrategico, 10)
            ).toString(),
          ) || 0}
        </b>
      ),
      separadorItemQtd: '',
      coberturaDias: item.coberturaDias
        ? formatarNumero(item.coberturaDias.toString())
        : '-',
      coberturaMeses: item.coberturaMeses
        ? formatarNumero(item.coberturaMeses.toString())
        : '-',
      separadorItemCobertura: '',
      fatorEmbalagem: item.fatorEmbalagem?.toString() || '-',
      qtdEmbalagem: item.qtdEmbalagem
        ? formatarNumero(item.qtdEmbalagem.toString())
        : '-',
      separadorItemValor: '',
      valorUntEmbalagem: item.valorUntEmbalagem ? (
        <b>
          {`R$ ${formatValorDinheiro(parseFloat(item.valorUntEmbalagem).toFixed(2))}`}
        </b>
      ) : (
        '-'
      ),
      separador: '',
      valorTotalEstimado:
        item.valorTotalEstimado && item.valorTotalEstimado != 0 ? (
          <b>
            {`R$ ${formatValorDinheiro(parseFloat(item.valorTotalEstimado).toFixed(2))}`}
          </b>
        ) : (
          '-'
        ),
    };
  });

  const handleDoubleClickCmm = useCallback(() => {
    onClickCalculaCmmReferencia();
  }, [onClickCalculaCmmReferencia]);

  const handleDoubleClickQtd = useCallback(() => {
    onClickCalculaQtdAdquirida();
  }, [onClickCalculaQtdAdquirida]);

  const handleClearFormProaqItem = () => {
    setFormProaqItem({
      id: '',
      numeroItem: numeroProximoItem,
      tipoCota: '',
      cmmInicial: '',
      cmmFim: '',
      cmmValorReferencia: '',
      qtdAdministrativa: '',
      qtdJudicial: '',
      qtdEstrategico: '',
      qtdOutro: '',
      valorPercentualEstrategico: '',
      isValorPercentualEstrategico: false,
      qtdTotal: '',
      fatorEmbalagem: '',
      qtdEmbalagem: '',
      coberturaDias: '',
      coberturaMeses: '',
      valorUntEmbalagem: '',
      valorTotalEstimado: '',
      observacoes: '',
      produto: '',
      produtoDAFId: 0,
      usuarioId: userID,
      proaqId: parseInt(fichaID, 10),
    });
    setProdutoId('0');
    setLogProaqItemData(undefined);
  };

  const handleShowModalItem = () => {
    setShowModalItem((prev) => !prev);
    handleClearFormProaqItem();
  };

  const handleChangeItem = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, type, checked, value } = event.target as HTMLInputElement;
    const newValue = value === '' ? 0 : parseInt(value, 10);

    if (type === 'checkbox') {
      setFormProaqItem((prev) => ({ ...prev, [name]: checked }));
      return;
    }
    if (name == 'produtoDAFId') {
      const produto = produtosDAFOptions.find(
        (option) => option.value.toString() === value,
      );
      setProdutoId(value);
      if (produto) {
        setFormProaqItem({
          ...formProaqItem,
          produtoDAFId: parseInt(produto.value, 10),
        });
      }
    } else if (name === 'numeroItem') {
      setNumeroProximoItem(newValue);
      setFormProaqItem({
        ...formProaqItem,
        numeroItem: newValue,
      });
    } else if (name === 'valorPercentualEstrategico') {
      setFormProaqItem({
        ...formProaqItem,
        [name]: formatoMoney(value),
      });
    } else if (
      name === 'cmmFim' &&
      !isDataMaiorOuIgual(formProaqItem.cmmInicial, value)
    ) {
      swalAttention('O CMM - Fim deve ser igual ou posterior ao CMM - Início.');
      return formProaqItem;
    } else {
      setFormProaqItem((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleRowClickItem = (id: string) => {
    handleShowModalItem();
    setUpdatingProaqItem(true);
    const filteredItem = itensData.find((item) => item.id == parseInt(id, 10));

    if (filteredItem) {
      setFormProaqItem({
        ...formProaqItem,
        id: filteredItem.id,
        numeroItem: filteredItem.numeroItem,
        tipoCota: filteredItem.tipoCota,
        cmmInicial: formatDateIntl(filteredItem.cmmInicio) ?? '',
        cmmFim: formatDateIntl(filteredItem.cmmFinal) ?? '',
        cmmValorReferencia:
          filteredItem.cmmValorReferencia &&
          filteredItem.cmmValorReferencia != null
            ? filteredItem.cmmValorReferencia
            : '',
        qtdAdministrativa:
          filteredItem.qtdAdministrativa &&
          filteredItem.qtdAdministrativa != null
            ? formatarNumero(filteredItem.qtdAdministrativa)
            : '',
        qtdJudicial:
          filteredItem.qtdJudicial && filteredItem.qtdJudicial != null
            ? formatarNumero(filteredItem.qtdJudicial.toString())
            : '',
        qtdEstrategico:
          filteredItem.qtdEstrategico && filteredItem.qtdEstrategico != null
            ? formatarNumero(filteredItem.qtdEstrategico.toString())
            : '',
        qtdOutro:
          filteredItem.qtdOutro && filteredItem.qtdOutro != null
            ? formatarNumero(filteredItem.qtdOutro.toString())
            : '',
        isValorPercentualEstrategico: false,
        qtdTotal:
          filteredItem.qtdAdministrativa &&
          filteredItem.qtdJudicial &&
          filteredItem.qtdEstrategico &&
          filteredItem.qtdAdministrativa != null &&
          filteredItem.qtdJudicial != null &&
          filteredItem.qtdOutro != null &&
          filteredItem.qtdEstrategico != null
            ? formatarNumero(filteredItem.qtdAdministrativa) +
              filteredItem.qtdJudicial +
              filteredItem.qtdEstrategico +
              filteredItem.qtdOutro
            : '',
        valorUntEmbalagem:
          filteredItem.valorUntEmbalagem &&
          filteredItem.valorUntEmbalagem != null
            ? parseFloat(filteredItem.valorUntEmbalagem).toFixed(4)
            : '',
        valorTotalEstimado:
          filteredItem.valorTotalEstimado &&
          filteredItem.valorTotalEstimado != null
            ? parseFloat(filteredItem.valorTotalEstimado).toFixed(4)
            : '',
        fatorEmbalagem:
          filteredItem.fatorEmbalagem && filteredItem.fatorEmbalagem != null
            ? filteredItem.fatorEmbalagem
            : '',
        qtdEmbalagem:
          filteredItem.qtdEmbalagem && filteredItem.qtdEmbalagem != null
            ? filteredItem.qtdEmbalagem
            : '',
        coberturaDias:
          filteredItem.coberturaDias && filteredItem.coberturaDias != null
            ? filteredItem.coberturaDias
            : '',
        coberturaMeses:
          filteredItem.coberturaMeses && filteredItem.coberturaMeses != null
            ? filteredItem.coberturaMeses
            : '',
        observacoes: filteredItem.observacoes,
        produto: filteredItem.produtoDAF.produtoFarmaceutico,
        produtoDAFId: filteredItem.produtoDAF.id,
        usuarioId: userID,
        proaqId: parseInt(fichaID, 10),
      });
      setProdutoId(filteredItem.produtoDAF.id.toString());
      setLogProaqItemData({
        id: filteredItem.id,
        usuarioRegistro: filteredItem.usuarioRegistro.nomeCompleto || '',
        usuarioAtualizacao: filteredItem.usuarioAtualizacao.nomeCompleto || '',
        dataRegistro: filteredItem.dataRegistro,
        dataAtualizacao: filteredItem.dataUltimaAtualizacao,
        nEdicoes: filteredItem.nEdicoes,
      });
    }
  };

  const validateFormProaqItem = (form: any): boolean => {
    const erros: string[] = [];
    const tipoCotaReservada = '2';
    const tipoCotaExclusiva = '3';

    if (!form.tipoCota || form.tipoCota == 0) {
      erros.push('<b>Tipo da Cota</b> é obrigatório.');
    }

    if (
      itensData &&
      itensData.find(
        (item) =>
          item.tipoCota?.toString() === form.tipoCota?.toString() &&
          item.produtoDAF?.id === form.produtoDAFId &&
          item.id != parseInt(form.id, 10),
      )
    ) {
      erros.push(
        `<b>Produto Farmacêutico</b> já cadastrado para o <b>Tipo da Cota - ${
          TipoCota[form.tipoCota].label
        }</b>`,
      );
    }

    if (form.tipoCota?.toString() == tipoCotaReservada) {
      if (
        itensData &&
        itensData.find(
          (item) =>
            item.tipoCota?.toString() === tipoCotaExclusiva &&
            item.produtoDAF?.id === form.produtoDAFId &&
            item.id != parseInt(form.id, 10),
        )
      ) {
        erros.push(
          `<b>Produto Farmacêutico</b> já cadastrado para o <b>Tipo da Cota - ${
            TipoCota[tipoCotaExclusiva].label
          }</b>`,
        );
      }
    }

    if (form.tipoCota?.toString() == tipoCotaExclusiva) {
      if (
        itensData &&
        itensData.find(
          (item) =>
            item.tipoCota?.toString() === tipoCotaReservada &&
            item.produtoDAF?.id === form.produtoDAFId &&
            item.id != parseInt(form.id, 10),
        )
      ) {
        erros.push(
          `<b>Produto Farmacêutico</b> já cadastrado para o <b>Tipo da Cota - ${
            TipoCota[tipoCotaReservada].label
          }</b>`,
        );
      }
    }

    if (form.produtoDAFId == 0) {
      erros.push('<b>Produto Farmacêutico</b> é obrigatório.');
    }

    if (!form.numeroItem) {
      erros.push('<b>Nº do Item</b> é obrigatório.');
    }
    if (
      itensData.find(
        (item) =>
          item.numeroItem == form.numeroItem &&
          item.id != parseInt(form.id, 10),
      )
    ) {
      erros.push('<b>Nº do Item</b> já cadastrado.');
    }

    if (erros.length > 0) {
      swalAttention(erros.join('<br>'));
      return false;
    }

    return true;
  };

  const handleCreateItem = async () => {
    try {
      const formAjustado = {
        ...formProaqItem,
        //cmmInicio: new Date(formProaqItem.cmmInicial),
        //cmmFinal: new Date(formProaqItem.cmmFim),
        cmmInicio: formProaqItem.cmmInicial || null,
        cmmFinal: formProaqItem.cmmFim || null,
        tipoCota: parseInt(formProaqItem.tipoCota, 10),
        cmmValorReferencia: parseInt(
          formProaqItem.cmmValorReferencia.replace('.', ''),
          10,
        ),
        qtdAdministrativa:
          parseInt(formProaqItem.qtdAdministrativa.replace('.', ''), 10) | 0,
        qtdJudicial:
          parseInt(formProaqItem.qtdJudicial.replace('.', ''), 10) | 0,
        qtdEstrategico:
          parseInt(formProaqItem.qtdEstrategico.replace('.', ''), 10) | 0,
        qtdOutro: parseInt(formProaqItem.qtdOutro.replace('.', ''), 10) | 0,
        fatorEmbalagem:
          typeof formProaqItem.fatorEmbalagem === 'string'
            ? parseInt(formProaqItem.fatorEmbalagem.replace('.', ''), 10)
            : formProaqItem.fatorEmbalagem,
        qtdEmbalagem: parseInt(formProaqItem.qtdEmbalagem.replace('.', ''), 10),
        coberturaDias: parseInt(
          formProaqItem.coberturaDias.replace('.', ''),
          10,
        ),
        coberturaMeses: parseInt(
          formProaqItem.coberturaMeses.replace('.', ''),
          10,
        ),
        valorUntEmbalagem: parseFloat(formProaqItem.valorUntEmbalagem),
        valorTotalEstimado: parseFloat(formProaqItem.valorTotalEstimado),
        numeroItem: numeroProximoItem,
      };
      const formEnvio = sanitizarForm(formAjustado);

      if (!validateFormProaqItem(formEnvio)) return;
      await createProaqItem(formEnvio);
      swalSave();
      fetchItens();
      setTimeout(() => handleShowModalItem(), 2000);
    } catch (error: any) {
      const errorMessage = error.message || 'Ocorreu um erro desconhecido.';
      swalErro(errorMessage);
    }
  };

  const handleUpdateItem = async () => {
    try {
      const formAjustado = {
        ...formProaqItem,
        // cmmInicio: new Date(formProaqItem.cmmInicial),
        // cmmFinal: new Date(formProaqItem.cmmFim),
        cmmInicio: formProaqItem.cmmInicial || null,
        cmmFinal: formProaqItem.cmmFim || null,
        tipoCota: parseInt(formProaqItem.tipoCota, 10),
        cmmValorReferencia: parseInt(
          formProaqItem.cmmValorReferencia.replace('.', ''),
          10,
        ),
        qtdAdministrativa:
          parseInt(formProaqItem.qtdAdministrativa.replace('.', ''), 10) | 0,
        qtdJudicial:
          parseInt(formProaqItem.qtdJudicial.replace('.', ''), 10) | 0,
        qtdEstrategico:
          parseInt(formProaqItem.qtdEstrategico.replace('.', ''), 10) | 0,
        qtdOutro: parseInt(formProaqItem.qtdOutro.replace('.', ''), 10) | 0,
        fatorEmbalagem: parseInt(formProaqItem.fatorEmbalagem, 10),
        qtdEmbalagem: parseInt(formProaqItem.qtdEmbalagem.replace('.', ''), 10),
        coberturaDias: parseInt(
          formProaqItem.coberturaDias.replace('.', ''),
          10,
        ),
        coberturaMeses: parseInt(
          formProaqItem.coberturaMeses.replace('.', ''),
          10,
        ),
        valorUntEmbalagem: parseFloat(formProaqItem.valorUntEmbalagem),
        valorTotalEstimado: parseFloat(formProaqItem.valorTotalEstimado),
      };
      const formEnvio = sanitizarForm(formAjustado);
      if (!validateFormProaqItem(formEnvio)) return;
      const response = await updateProaqItem(formEnvio.id, formEnvio);

      swalAtualizacao('Sucesso', response.message);
      fetchItens();
      setTimeout(() => {
        handleShowModalItem();
      }, 2000);
    } catch (error: any) {
      const errorMessage = error.message || 'Ocorreu um erro desconhecido.';
      swalErro(errorMessage);
    }
  };

  const handleDeleteItem = async () => {
    const result = await swalDelete();

    if (result) {
      try {
        await deleteProaqItem(formProaqItem.id);
        swalDeleteConfirm();
        setTimeout(() => {
          handleShowModalItem();
          fetchItens();
        }, 2000);
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.message ||
          'Não foi possível excluir o registro. Tente novamente.';
        swalErro(errorMessage);
      }
    }
  };

  return (
    <>
      <BoxConteudo
        boxTitulo="Itens a Serem Adquiridos"
        fechado={fichaID === 'undefined'}
      >
        <CustomRow>
          <CustomCol width="100%">
            <Tabela
              headers={headersProaqItens}
              dataTable={tableDataItem}
              displayValues={valoresExibidosProaq}
              onRowClick={handleRowClickItem}
              dataOffCanvas={offCanvasDataItem}
              showPagination={false}
            />
          </CustomCol>
        </CustomRow>
        <Col className="d-flex justify-content-end">
          <CustomButton
            label="Inserir item"
            bgColor={theme.colors.botaoAcao.azul}
            textColor="#ffffff"
            onClick={() => {
              setUpdatingProaqItem(false);
              handleShowModalItem();
            }}
            disabled={fichaID === 'undefined'}
          />
        </Col>
      </BoxConteudo>

      <CustomOffcanvas
        title="Item do Processo Aquisitivo"
        show={showModalItem}
        onClose={handleShowModalItem}
        width="80vw"
      >
        <WorkspaceFicha>
          <Log
            {...logProaqItemData}
            entity="TB_PROAQ_ITEM"
            id={Number(formProaqItem.id)}
          />
          <BoxConteudo boxTitulo="Item do Processo Aquisitivo">
            <CustomRow style={{ marginTop: '10px' }}>
              <CustomCol width="10%">
                <CustomInputInt
                  name="numeroItem"
                  title="Nº do Item"
                  required
                  onChange={handleChangeItem}
                  value={
                    updatingProaqItem
                      ? formProaqItem.numeroItem
                      : numeroProximoItem
                  }
                />
              </CustomCol>
              <CustomCol width="15%">
                <Select
                  name="tipoCota"
                  value={formProaqItem.tipoCota}
                  options={TipoCota}
                  onChange={handleChangeItem}
                  label="Tipo da Cota"
                  required
                />
              </CustomCol>
              <CustomCol>
                <Select
                  name="produtoDAFId"
                  value={produtoId}
                  options={produtosDAFOptions}
                  label="Produto Farmacêutico"
                  onChange={handleChangeItem}
                  required
                />
              </CustomCol>
              <CustomCol width="15%">
                <CustomInputInt
                  name="fatorEmbalagem"
                  title="Fator Embalagem"
                  onChange={handleChangeItem}
                  value={formProaqItem.fatorEmbalagem}
                />
              </CustomCol>
            </CustomRow>

            <CustomRow
              style={{ marginTop: '10px', justifyContent: 'flex-start' }}
            >
              <CustomCol
                width="15%"
                helpText="Clique duplo para calcular a partir da Qtd. Administrativa: (QtAdm / 365.5 * 30)"
              >
                <Input
                  name="cmmValorReferencia"
                  value={formatarNumero(formProaqItem.cmmValorReferencia)}
                  id="cmmValorReferencia"
                  label="CMM - Ref. do processo"
                  onChange={handleChangeItem}
                  onDoubleClick={handleDoubleClickCmm}
                  textAlign="right"
                />
              </CustomCol>
              <CustomCol width="15%">
                <Input
                  name="cmmInicial"
                  value={formProaqItem.cmmInicial}
                  id="cmmInicial"
                  type="date"
                  label="CMM - Data de início"
                  onChange={handleChangeItem}
                />
              </CustomCol>
              <CustomCol width="15%">
                <Input
                  name="cmmFim"
                  value={formProaqItem.cmmFim}
                  id="cmmFim"
                  type="date"
                  label="CMM - Data final"
                  onChange={handleChangeItem}
                />
              </CustomCol>
              <CustomCol></CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol helpText="Clique duplo para calcular a partir do CMM - Referência: (CmmRef / 30 * 365.5)">
                <Input
                  name="qtdAdministrativa"
                  value={formatarNumero(formProaqItem.qtdAdministrativa)}
                  id="qtdAdministrativa"
                  label="Qtd Administrativa"
                  textAlign="right"
                  onChange={handleChangeItem}
                  onDoubleClick={handleDoubleClickQtd}
                />
              </CustomCol>
              <CustomCol
                width="15%"
                helpText="Definir quantidade por percentual"
              >
                <div
                  style={{
                    marginBottom: '5px',
                    paddingTop: '0px',
                    color: theme.colors.botaoConfirmar,
                  }}
                >
                  Valor Percentual Estratégico
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '2px',
                  }}
                >
                  <Checkbox
                    name="isValorPercentualEstrategico"
                    label=""
                    checked={formProaqItem.isValorPercentualEstrategico}
                    style={{ fontSize: '1.5rem' }}
                    onChange={handleChangeItem}
                  ></Checkbox>
                  <InputGroup>
                    <Form.Control
                      aria-label="Dollar amount (with dot and two decimal places)"
                      name="valorPercentualEstrategico"
                      value={
                        formProaqItem.isValorPercentualEstrategico
                          ? formProaqItem.valorPercentualEstrategico
                          : ''
                      }
                      id="valorPercentualEstrategico"
                      onChange={handleChangeItem}
                      readOnly={!formProaqItem.isValorPercentualEstrategico}
                      style={{
                        borderTopLeftRadius: '10px',
                        borderBottomLeftRadius: '10px',
                        border: `1px solid #C5D4EB`,
                        textAlign: 'right',
                        backgroundColor:
                          !formProaqItem.isValorPercentualEstrategico
                            ? '#ebf0f8'
                            : 'white',
                        cursor: !formProaqItem.isValorPercentualEstrategico
                          ? 'default'
                          : 'text',
                        userSelect: !formProaqItem.isValorPercentualEstrategico
                          ? 'none'
                          : 'text',
                      }}
                    />
                    <InputGroup.Text
                      style={{
                        borderTopRightRadius: '10px',
                        borderBottomRightRadius: '10px',
                        border: `1px solid #C5D4EB`,
                        textAlign: 'center',
                        backgroundColor:
                          !formProaqItem.isValorPercentualEstrategico
                            ? '#ebf0f8'
                            : 'white',
                        cursor: !formProaqItem.isValorPercentualEstrategico
                          ? 'default'
                          : 'text',
                        userSelect: !formProaqItem.isValorPercentualEstrategico
                          ? 'none'
                          : 'text',
                      }}
                    >
                      %
                    </InputGroup.Text>
                  </InputGroup>
                </div>
              </CustomCol>
              <CustomCol>
                <Input
                  name="qtdEstrategico"
                  value={formatarNumero(formProaqItem.qtdEstrategico) ?? ''}
                  id="qtdEstrategico"
                  label="Qtd Estoque Estratégico"
                  textAlign="right"
                  onChange={handleChangeItem}
                  readonly={formProaqItem.isValorPercentualEstrategico}
                />
              </CustomCol>

              <CustomCol>
                <Input
                  name="qtdJudicial"
                  value={formatarNumero(formProaqItem.qtdJudicial)}
                  id="qtdJudicial"
                  label="Qtd Judicial"
                  textAlign="right"
                  onChange={handleChangeItem}
                />
              </CustomCol>
              <CustomCol>
                <Input
                  name="qtdOutro"
                  value={formatarNumero(formProaqItem.qtdOutro)}
                  id="qtdOutro"
                  label="Qtd Outro"
                  textAlign="right"
                  onChange={handleChangeItem}
                />
              </CustomCol>

              <CustomCol>
                <Input
                  name="qtdTotal"
                  value={formProaqItem.qtdTotal}
                  id="qtdTotal"
                  label="Qtd Total"
                  textAlign="right"
                  readonly
                />
              </CustomCol>
              <CustomCol
                width="110px"
                helpText="Calculado a partir da fórmula: Qtd Administrativa / (CMM - Ref / 30)"
              >
                <Input
                  name="coberturaDias"
                  value={formProaqItem.coberturaDias}
                  id="coberturaDias"
                  label="Cobert. Dias"
                  textAlign="right"
                  readonly
                />
              </CustomCol>
              <CustomCol
                width="120px"
                helpText="Calculado a partir da fórmula: CoberturaDias / 30"
              >
                <Input
                  name="coberturaMeses"
                  value={formProaqItem.coberturaMeses}
                  id="coberturaMeses"
                  label="Cobert. Meses"
                  textAlign="right"
                  readonly
                />
              </CustomCol>
            </CustomRow>

            <CustomRow
              style={{ marginTop: '10px', justifyContent: 'flex-start' }}
            >
              <CustomCol width="200px">
                <CustomInputMoney
                  name="valorUntEmbalagem"
                  value={formProaqItem.valorUntEmbalagem}
                  title="Valor Unitário (R$)"
                  onChange={handleChangeItem}
                />
              </CustomCol>
              <CustomCol width="18%">
                <Input
                  name="valorTotalEstimado"
                  value={formatValorDinheiro(
                    formProaqItem.valorTotalEstimado.toString(),
                  )}
                  id="valorTotalEstimado"
                  label="Valor Total (R$)"
                  textAlign="right"
                  readonly
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <Col>
                <Input
                  id="observacoes"
                  type="textarea"
                  name="observacoes"
                  value={formProaqItem.observacoes}
                  label="Observações"
                  linhas={5}
                  onChange={handleChangeItem}
                />
              </Col>
            </CustomRow>
          </BoxConteudo>
          <BarraInferiorBotoes>
            {updatingProaqItem ? (
              <WorkspaceBotoes
                delete
                onClickDelete={handleDeleteItem}
                save
                onClickSave={handleUpdateItem}
              />
            ) : (
              <WorkspaceBotoes inserir onClickInserir={handleCreateItem} />
            )}
          </BarraInferiorBotoes>
        </WorkspaceFicha>
      </CustomOffcanvas>
    </>
  );
};

export default ItensAquisicao;
